<template>
  <v-container fluid justify-start>
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredMessages"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dark dense flat :color="toolbarColor">

          <app-back-button v-bind:color="toolbarColorDark" />

          <v-toolbar-title :class="titleClass">{{ title }}</v-toolbar-title>

        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">
          <!-- Kiosk Dropdown -->
          <template>
            <v-col v-if="showKiosk" cols="12" sm="3">
            <kiosk-combobox
              v-model="kioskId"
              :options="kiosks"
              :label="getExpression('Label', 'Kiosk')"
              :placeholder="getExpression('Label', 'SelectKiosk')"
              :disabled="disableKiosk || isLoading"
              class="mr-3"
            />
            </v-col>
          </template>

          <!-- Resident Dropdown -->
          <v-col v-if="showResident" cols="12" sm="3">
            <v-select
              v-model="residentId"
              item-value="id"
              item-text="name"
              :items="residents"
              :disabled="disableResident"
              :placeholder="getExpression('Label', 'SelectResident')"
              :label="getExpression('Label', 'Resident')"
              class="mr-3"
            ></v-select>
          </v-col>

          <!-- User Dropdown -->
          <v-col cols="12" sm="2">
            <v-select
              v-model="userId"
              item-value="id"
              item-text="name"
              :items="filteredUsers"
              :label="getExpression('Label', 'User')"
              :disabled="disableUser"
            ></v-select>
          </v-col>

          <!-- Message Type Dropdown -->
          <v-col cols="12" sm="2">
            <v-select
              v-model="messageTypeId"
              item-value="id"
              item-text="description"
              :items="messageTypes"
              :label="getExpression('Label', 'Direction')"
              :disabled="disableMessageType"
              class="ml-3"
            ></v-select>
          </v-col>

          <!-- Search Subject -->
          <v-col cols="12" :sm="searchColWidth">
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Subject')"
              :disabled="disableSearch"
              clearable
              class="ml-3"
            >
            </v-text-field>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.message="{ item }">
        <template v-if="getMediaBlobName(item) !== null">
          <router-link to="/messageManager">
            <v-badge
              bordered
              overlap
              right
              :icon="getMediaTypeIcon(item.messageBlobs[0])"
              color="grey"
              class="mt-2"
              >
              <span @click="preview(item.messageBlobs[0])">
                <v-img
                  width="40"
                  height="40"
                  :src="getThumbnailUrl(item.messageBlobs[0])"
                  alt=""
                ></v-img>
              </span>
            </v-badge>
          </router-link>
        </template>

        <template v-else>
          <template v-if="getVoiceBlobName(item) !== null">
            <v-btn icon @click="previewVoice(item)">
              <v-icon large color="grey darken-3">voicemail</v-icon>
            </v-btn>
          </template>

          <template v-if="item.text != null">
            <v-btn
              v-if="!isNullOrUndefined(item.text)"
              icon
              @click="showText(item.text)"
              >
              <v-icon large color="grey darken-3">subject</v-icon>
            </v-btn>
          </template>
        </template>
      </template>

      <template v-slot:item.userPicture="{ item }">
        <v-img width="50" height="50" :src="getUserPicture(item.userPicture)"></v-img>
      </template>

      <template v-slot:item.residentName="{ item }">
        <span class="text-left">{{ item.residentName }}</span>
      </template>
      
      <template v-slot:item.residentPicture="{ item }">
        <v-img width="50" height="50" :src="item.residentPicture"></v-img>
      </template>

      <template v-slot:item.subject="{ item }">
        <span class="text-left">{{ getSubject(item) }}</span>
      </template>
      
      <template v-slot:item.firstOpened="{ item }">
        <span class="text-left"><app-date :date="item.firstOpened" /></span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left"><app-date :date="item.createDate" /></span>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon @click="remove(item)" class="text-center">delete</v-icon>
      </template>
    </v-data-table>

    <!-- Media Previewer -->
    <v-dialog v-model="previewDialog" max-width="800px">
      <media-previewer
        v-bind:type="previewType"
        v-bind:url="previewUrl"
        v-bind:title="previewTitle"
        v-on:close="closePreview"
      >
      </media-previewer>
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import MediaPreviewer from '@/components/MediaPreviewer'
import KioskCombobox from '@/components/KioskCombobox'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'
import { mapGetters } from 'vuex'
import { AppColor, MessageTypeId, KioskTypeId, UserRole, ImageUri, ThumbnailUrl, Base64Image, LanguageId } from '@/config.js'

export default {
  components: {
    MediaPreviewer,
    KioskCombobox,
    AppDate,
    AppBackButton
  },
  name: 'MessageManager',
  data() {
    return {
      // colors
      appColor_Progress: AppColor.Progress,
      appColor_Light: AppColor.Light,
      appColor_Dark: AppColor.Dark,
      appColor_Genie: AppColor.KioskTypeGenieLight,
      appColor_GenieDark: AppColor.KioskTypeGenie,
      appColor_Resident: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,

      // message types
      messageTypeId_Sent: MessageTypeId.UserToResident,
      messageTypeId_Received: MessageTypeId.ResidentToUser,

      // kiosk types
      kioskTypeId_Genie: KioskTypeId.Genie,
      
      // roles
      role_Manager: UserRole.Manager,

      // url / uri
      imageUri_NoAvatar: ImageUri.NoAvatar,
      thumbnailUrl_Genie: ThumbnailUrl.Genie,

      // icons
      base64Image_AudioIcon: Base64Image.AudioIcon,

      isLoading: true,
      isDeleting: false,
      search: '',
      editDialog: false,
      detailDialog: false,
      previewDialog: false,
      previewType: null,
      previewUrl: null,
      previewTitle: null,
      kioskId: null,
      kiosks: [],
      userId: '',
      users: [],
      residentId: null,
      residents: [],
      messages: [],
      messageTypeId: MessageTypeId.UserToResident,
      messageBlobs: [],
      editedIndex: -1,
      editedMessage: {
        id: -1,
        messageThreadId: null,
        userId: null,
        userPicture: null,
        residentId: null,
        residentName: '',
        residentPicture: null,
        subject: null,
        text: null,
        isOpened: false,
        createDate: null
      },
      defaultMessage: {
        id: -1,
        messageThreadId: null,
        userId: null,
        userPicture: null,
        residentId: null,
        residentName: '',
        residentPicture: null,
        subject: null,
        text: null,
        isOpened: false,
        createDate: null
      },
      pictures: null
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'activeResident',
      'activeKioskItem',
      'activeResidentKioskItem',
      'isAdmin',
      'isDomainManager',
      'isKioskManager',
      'isResidentManager',
      'previousRoute',
      'routeStack',
      'locale']),
    title () {
      if (this.isFromKioskDashboard && this.isFromResidentDashboard) {
       return `${this.activeResidentKioskName} / ${this.activeResidentName}`
      } else if (this.isFromResidentDashboard) {
        return this.activeResidentName
      } else if (this.isFromKioskDashboard) {
        return this.activeKioskName
      }
      return 'Messages'
    },
    noDataText () {
      if (this.isLoading) return 'One moment...'

      if (this.messages.length === 0 && this.residentId !== null) {
        return 'You have no messages'
      }

      if (this.messages.length === 0 && this.residentId === null) {
        return 'No resident selected'
      }

      return ''
    },
    allText () {
      return this.getExpression('Label', 'All')
    },
    toolbarColor () {
      if (this.isFromResidentDashboard)
        return this.appColor_Resident
      else if (this.isFromKioskDashboard) {
        return this.appColor_Genie
      } else return this.appColor_Light
    },
    toolbarColorDark () {
      if (this.isFromResidentDashboard)
        return this.appColor_ResidentDark
      else if (this.isFromKioskDashboard) {
        return this.appColor_GenieDark
      } else return this.appColor_Dark
    },
    isFromResidentDashboard () {
      if (!this.isValidSession) return false
      return this.isResidentManager && this.profile.residentIds.length === 1
        ? this.routeStack.includes('Home')
        : this.routeStack.includes('ResidentDashboard')
    },
    isFromKioskDashboard () {
      if (!this.isValidSession) return false
      return (this.isKioskManager && this.profile.kioskIds.length === 1) || this.isDomainManager
        ? this.routeStack.includes('Home')
        : this.routeStack.includes('KioskDashboard')
    },
    isFromDashboard () {
      return this.isFromKioskDashboard || this.isFromResidentDashboard
    },
    isDashboardValid () {
      const kioskId = this.kioskId ?? 0
      const residentId = this.residentId ?? 0

      if (this.isFromDashboard) {
        return (this.isFromKioskDashboard && kioskId > 0)
        || (this.isFromResidentDashboard && residentId > 0)
      } else {
        return kioskId > 0
      }
    },    
    activeKioskName () {
      if (this.isNullOrUndefined(this.activeKioskItem)) return null
      return this.activeKioskItem.serialNumber
    },
    activeResidentName () {
      if (this.isNullOrUndefined(this.activeResident)) return null
      return this.activeResident.name
    },
    activeResidentKioskName () {
      if (this.isNullOrUndefined(this.activeResidentKioskItem)) return null
      return this.activeResidentKioskItem.serialNumber
    },
    messageTypes () {
      var messageTypes = []
      messageTypes.push({
        id: 1,
        description:
          this.profile.role == 'Guest' 
          ? this.getExpression('Label', 'ToResident')
          : this.getExpression('Label', 'UserToResident')
      })
      messageTypes.push({
        id: 2,
        description:
          this.profile.role == 'Guest' 
          ? this.getExpression('Label', 'FromResident')
          : this.getExpression('Label', 'ResidentToUser')
      })
      return messageTypes
    },
    filteredMessages () {
      const self = this
      return self.messages.filter((value) => {
        return (
          (value.residentId === self.residentId || self.residentId === 0) &&
          (value.userId === self.userId || self.userId === '') &&
          value.messageTypeId === self.messageTypeId
        )
      })
    },
    filteredUsers () {
      var users = this.buildSortedUsers()
      if (!users.map(u => u.id).includes('')) {
        users.splice(0, 0, { id: '', name: this.allText })
      }
      return users
    },
    headers () {
      var headers = []
      headers.push({
        text: this.getExpression('Label', 'Message'),
        value: 'message',
        align: 'left',
        width:
          this.profile.role == 'Admin' || this.profile.role == 'Manager'
            ? '8%'
            : '10%',
        class: 'pr-0'
      })
      if (this.profile.role == 'Admin' || this.profile.role == 'Manager') {
        headers.push({
          value: 'userPicture',
          align: 'center',
          width: '5%',
          sortable: false,
          class: 'pr-0'
        })
        headers.push({
          text: this.messageTypeId === this.messageTypeId_Sent
            ? this.getExpression('Label', 'From')
            : this.getExpression('Label', 'To'),
          value: 'userNickname',
          align: 'left',
          width: '12%',
          class: 'pr-0'
        })
      }
      headers.push({
        value: 'residentPicture',
        align: 'center',
        width: '5%',
        sortable: false,
        class: 'pr-0'
      })
      headers.push({
        text: this.messageTypeId === this.messageTypeId_Sent
          ? this.getExpression('Label', 'To')
          : this.getExpression('Label', 'From'),
        value: 'residentName',
        align: 'left',
        width: this.profile.role == 'Admin' || this.profile.role == 'Manager'
            ? '16%'
            : '22%',
        class: 'pr-0'
      })
      headers.push({
        text: this.getExpression('Label', 'Subject'),
        value: 'subject',
        align: 'left',
        width: '20%',
        class: 'pr-0'
      })
      headers.push({
        text: this.getExpression('Label', 'Sent'),
        value: 'createDate',
        align: 'left',
        width: '18%',
        class: 'pr-0'
      })
      headers.push({
        text: this.getExpression('Label', 'Opened'),
        value: 'firstOpened',
        align: 'left',
        width: this.profile.role == 'Admin' || this.profile.role == 'Manager'
            ? '18%'
            : '19%',
        class: 'pr-0'
      })
      headers.push({
        value: 'action',
        align: 'center',
        sortable: false,
        width: '1%',
        class: 'pr-0'
      })
      return headers
    },
    residentMessages () {
      const self = this
      return self.messages.filter((value) => {
        return (
          (value.residentId === self.residentId || self.residentId === 0) &&
          value.messageTypeId === self.messageTypeId
        )
      })
    },

    // rules
    disableKiosk () {
      return this.kiosks.length <= 1 || this.isLoading
    },
    disableResident () {
      if (this.isFromKioskDashboard) return this.isLoading
      return this.residents.length <= 1 || this.isLoading
    },
    disableUser () {
      return this.filteredUsers.length === 1 || this.isLoading || !this.isDashboardValid
    },
    disableMessageType () {
      return this.isLoading || !this.isDashboardValid
    },
    disableSearch () {
      return this.isLoading || !this.isDashboardValid
    },
    showKiosk () {
      return this.isAdmin && !(this.isFromKioskDashboard || this.isFromResidentDashboard)
    },
    showResident () {
      return !this.isFromResidentDashboard
    },

    // style
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    },
    searchColWidth () {
      return this.showKiosk
        ? this.showResident
          ? '2' 
          : '5'
        : this.showResident
          ? '5'
          : '8'
    },
    createButtonWidth () {
      return this.profile.languageId === LanguageId.English
        ? '140px'
        : '200px'
    }
  },
  methods: {
    loadData () {
      if (!this.isValidSession) return

      const self = this
      self.isLoading = true
      self.users = [{ id: '', name: self.allText }]

      // dashboard mode
      if (self.isFromKioskDashboard) {
        self.kioskId = self.activeKioskItem.id
      } else if (self.isFromResidentDashboard) {
        self.residentId = self.activeResident.id

      // admin mode
      } else {
        self.isLoading = true
        appService.getKioskComboboxOptions(self.kioskTypeId_Genie).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            self.kiosks = data.options
            self.kioskId = 0
          }
          self.isLoading = false
        }).catch(e => { self.handleCriticalError(e) })
      }
    },

    buildSortedUsers () {
      const self = this

      if (self.messages.length === 0) {
        return [{ id: '', name: self.getExpression('Label', 'All') }]
      } else {
        const users = self.residentMessages.map((m) => ({
          id: m.userId,
          name: m.userNickname
        }))
        return users.filter(self.onlyUnique).sort((a, b) => {
          // if (a.name === null || b.name === null) {
          //   console.log(a.name)
          // }
          var nameA = a.name.toLowerCase(),
            nameB = b.name.toLowerCase()
          if (nameA < nameB) return -1
          if (nameA > nameB) return 1
          return 0
        })
      }
    },

    loadMessages () {
      const self = this

      self.isLoading = true
      const profile = self.getProfileModel()
      appService.getProfileMessages(profile).then((data) => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.messages = data.messages
          self.userId = ''
          self.isLoading = false
          self.populatePictures()
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    getProfileModel () {
      var profileModel = Object.assign({}, this.profile)

      profileModel.kioskIds = null
      profileModel.residentIds = null
      profileModel.defaultResidentId = null

      if (this.residentId > 0) {
        profileModel.residentIds = [this.residentId]
        profileModel.defaultResidentId = this.residentId
      } else if (this.kioskId > 0) {
        profileModel.kioskIds = [this.kioskId]
      }

      return profileModel
    },

    loadPictures () {
      const self = this
      const ids = self.residents.map(r => r.id)
      
      appService.getResidentPicturesByIds(ids).then(data => {
        self.pictures = data.pictures
        self.populatePictures()
      }).catch(e => { self.handleCriticalError(e) })
    },

    populatePictures () {
      const self = this
      self.messages.forEach(m => {
        m.residentPicture = self.getPicture(m.residentId)
      })
    },

    getPicture (id) {
      if (this.pictures === null) return ''
      const self = this
      var pictures = self.pictures.filter((value) => {
        return value.residentId === id
      })
      if (pictures.length > 0) {
        return pictures[0].base64
      }
      return this.imageUri_NoAvatar
    },

    remove (item) {
      const self = this
      var dialogMessage

      const mediaType = item.messageBlobs.length > 0
        ? this.getMediaType(item.messageBlobs[0].contentType)
        : 'text'

      const isImageOrVideo = mediaType === 'image' || mediaType === 'video'
      const isVoice = mediaType === 'audio'

      const subject = isImageOrVideo
        ? item.subject
        : isVoice
          ? "Voice message"
          : "Text message"

      dialogMessage = `<b>${subject}</b>`
      
      self.$confirm.show({
        title: self.getExpression('FormTitle', 'DeleteMessage'),
        text: dialogMessage,
        onConfirm: () => {
          self.isDeleting = true

          appService.deleteMessage(item.id).then((data) => {
            self.isDeleting = false
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              data.deletedIds.forEach((id) => {
                const deletedMessages = self.messages.filter((value) => {
                  return value.id === id
                })
                if (deletedMessages.length > 0) {
                  const indexDeleted = self.messages.indexOf(deletedMessages[0])
                  self.messages.splice(indexDeleted, 1)
                }
              })
            }
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    showDetail (item) {
      const self = this

      self.editedIndex = this.messages.indexOf(item)
      self.editedMessage = Object.assign({}, item)
      self.cloneMessageBlobs()
      self.detailDialog = true
      setTimeout(() => {
        self.populateThumbnails()
      }, 300)
    },

    cloneMessageBlobs () {
      this.messageBlobs = []
      var id = this.editedMessage.id
      const messages = this.messages.filter((value) => {
        return value.id === id
      })

      if (messages.length > 0) {
        const message = messages[0]
        message.messageBlobs.forEach((b) => {
          this.messageBlobs.push({
            id: b.id,
            title: b.title,
            description: b.description,
            blobName: b.blobName,
            uri: b.uri,
            containerName: b.containerName,
            containerOwner: b.containerOwner,
            contentType: b.contentType,
            size: b.size,
            thumbnail: b.thumbnail,
            createDate: b.createDate
          })
        })
      }
    },

    detailDone ({ messageBlobs }) {
      const self = this

      const m = self.messages.filter((value) => {
        return value.id === self.editedMessage.id
      })[0]

      m.messageBlobs = []
      messageBlobs.forEach((b) => {
        m.messageBlobs.push({
          id: b.id,
          title: b.title,
          description: b.description,
          blobName: b.blobName,
          uri: b.uri,
          containerName: b.containerName,
          containerOwner: b.containerOwner,
          contentType: b.contentType,
          size: b.size,
          thumbnail: b.thumbnail,
          createDate: b.createDate
        })
      })
      self.detailDialog = false
    },

    closeDialog () {
      const self = this
      self.editDialog = false
    },

    getVoiceBlobName(item) {
      const mediaType = item.messageBlobs.length > 0
        ? this.getMediaType(item.messageBlobs[0].contentType)
        : null

      return item.messageBlobs.length > 0
        ? mediaType === 'audio'
          ? item.messageBlobs[0].blobName
          : null
        : null
    },

    getMediaBlobName(item) {
      const mediaType = item.messageBlobs.length > 0
        ? this.getMediaType(item.messageBlobs[0].contentType)
        : null

      return item.messageBlobs.length > 0
        ? mediaType !== 'audio'
          ? item.messageBlobs[0].blobName
          : null
        : null
    },

    getUserPicture(picture) {
      return picture !== null
        ? picture
        : this.imageUri_NoAvatar
    },

    getThumbnailUrl (item) {
      const mediaType = this.getMediaType(item.contentType)
      if (mediaType !== 'audio') {
        const blobName = item.blobName
        const ext = blobName.split('.').pop()
        const thumnailName = blobName.replace(ext, 'jpg')
        return `${this.thumbnailUrl_Genie}/${item.containerName}/${thumnailName}`
      } else {
        item.thumbnail = this.base64Image_AudioIcon
        return this.base64Image_AudioIcon
      }
    },

    getSubject (item) {
      const mediaType = item.messageBlobs.length > 0
        ? this.getMediaType(item.messageBlobs[0].contentType)
        : 'text'

      return item.isInitiator
        ? mediaType === 'audio' && this.messageTypeId === this.messageTypeId_Received
          ? null
          : item.subject
        : `Re: ${item.subject}`
    },

    preview (item) {
      const self = this
      const containerName = item.containerName
      const blobName = item.blobName
      const contentType = item.contentType
      appService.getBlobUrl(containerName, blobName).then((data) => {
        const mediaType = self.getMediaType(contentType)
        self.previewUrl = data
        self.previewType = mediaType
        self.previewTitle = item.title
        self.previewDialog = true
      }).catch(e => { self.handleCriticalError(e) })
    },

    previewVoice (item) {
      const self = this
      if (item.residentId > 0) {
        appService.getBlobUrl(`r-${item.residentId}`, item.messageBlobs[0].blobName).then((data) => {
          self.previewUrl = data
          self.previewType = 'audio'
          self.previewTitle = item.messageBlobs[0].title
          self.previewDialog = true
          if (item.userId === self.profile.id && self.messageTypeId === self.messageTypeId_Received) {
            appService.logVoiceMessage(item.id).then(()=>{
              if (item.firstOpened === null) {
                item.firstOpened = String(new Date())
              }
            })
          }
        }).catch(e => { self.handleCriticalError(e) })
      }
    },

    showText (text) {
      this.$alert.show({
        text: `<pre>${text}</pre>`,
        icon: 'subject',
        title: 'Text Message'
      })
    },
    
    closePreview () {
      this.previewUrl = null
      this.previewDialog = false
    },

    customFilter (value, search, item) {
      const subject = this.getSubject(item)
      const subjectSafe = subject !== null
        ? subject
        : ''

      return (
        (value != null &&
          search != null &&
          item.residentName
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1) ||
          subjectSafe
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1
        )
    },

    getMediaTypeIcon (item) {
      const mediaType = this.getMediaType(item.contentType)

      if (mediaType === 'image') {
        return 'camera_alt'
      } else if (mediaType === 'video') {
        return 'videocam'
      } else {
        return 'paperclip'
      }
    },

    loadKioskResidents () {
      return new Promise(resolve => {
        this.residents = []
        this.isLoading = true

        appService.getKioskResidents(this.kioskId).then(data => {
          if (data.errorSeverity !== null) {
            this.handleError(data)
          } else {
            this.residents = data.residents.map(r => { return {
              id: r.id,
              kioskId: r.kioskId,
              kioskTypeId: r.kioskTypeId,
              name: r.id === 0
                ? self.allText
                : r.name,
              keyId: r.keyId,
              beaconId: r.beaconId
            }})

            this.residents.unshift({ id: 0, name: this.allText })
            this.residentId = this.residents.length === 1
                ? this.residents[0].id
                : 0

            this.loadPictures()
          }
          resolve()
        })
      })
    },

    setSelectedUser () {
      if (this.userId === '') return

      const self = this
      const userIds = self.filteredMessages.map((m) => m.userId)
      const userExists = userIds.includes(self.userId)
      if (!userExists) {
        self.userId = ''
      }
    },

    handleError (result) {
      this.$error.show({
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData ()
  },
  watch: {
    kioskId (newValue, oldValue) {
      const self = this

      if (oldValue !== newValue) {
        if (self.isFromResidentDashboard) {
          // trigger the residentId watch
          self.residentId = self.activeResident.id
          return
        }
      }

      if (self.kioskId > 0) {
        self.messages = []
        self.loadKioskResidents().then(() => {
          if (self.residents.length > 0) {
            self.loadMessages()
          }
        })
      } else {
        this.messages = []
        this.residents = []
      }
    },

    residentId () {
      if (this.isFromResidentDashboard) {
        this.loadMessages()
        this.setSelectedUser()
      }
    },
    
    messageTypeId () {
      this.setSelectedUser()
    },
    
    isValidSession (val) {
      if (val === null) return
      this.loadData()
    }
  }
}
</script>